/**
 * CommentBody — render rico de comentário no card Corretiva.
 *
 * BUG-B1.6.2 (Matheus 2026-05-28 noite, 3ª iteração):
 *   - Renderiza markdown (**bold**, *itálico*, `código`) via renderMarkdown
 *     sanitizado (mesmo helper do timeline de Comentários do Comercial).
 *   - Remove linhas separadoras `---` / `———` (não fazem sentido no card).
 *   - Detecta R2 keys de fotos do chamado (`chamados/.../*.jpg|png|heic|...`)
 *     e renderiza `<img>` inline via presigned URL — admin VÊ a foto na
 *     timeline do comentário (não só como texto cru).
 *
 * Hoje suporta apenas a foto principal do deal (passada via prop). Pra
 * comentários com múltiplas fotos diferentes, expandir pra batch endpoint
 * `POST /api/v1/admin/chamados/foto-urls` no futuro.
 *
 * NÃO REVERTER:
 *   - Sanitização DOMPurify já no renderMarkdown (XSS-safe).
 *   - `dangerouslySetInnerHTML` é OK porque HTML vem do renderMarkdown
 *     sanitizado + substituição controlada de img tags com src validado.
 *   - Image src é o presigned URL R2 fetched no parent (não input do user).
 */

import { useMemo } from 'react';

import { renderMarkdown } from '@/lib/render-markdown';
import { resolveMentions } from '@/lib/mentions';
import { useUsersLookup } from '@/lib/admin-store';

interface CommentBodyProps {
  /** Texto cru do comentário (markdown). */
  corpo: string;
  /** R2 key principal do chamado — usado pra REMOVER refs ao key no texto. */
  fotoR2Key: string | null | undefined;
  /** Presigned URL — não usado (mantido pra compat de API, será removido futuro). */
  fotoUrl?: string | null | undefined;
}

export function CommentBody({ corpo, fotoR2Key }: CommentBodyProps) {
  // B2 Lote 3 #23: resolve @user-id → @Nome (lookup leve, funciona p/ não-admin).
  const users = useUsersLookup();
  const html = useMemo(() => {
    if (!corpo) return '';

    // 0) Resolve @menções (id → nome) antes de qualquer outro processamento.
    let cleaned = resolveMentions(corpo, users);

    // 1) Strip linhas separadoras (---, —, ———) isoladas
    cleaned = cleaned.replace(/^[—–-]{2,}\s*$/gm, '');

    // 2) Remove a linha "📷 Foto anexada: `<R2_KEY>`" inteira — Matheus pediu
    //    pra NÃO mostrar foto no comentário (causava lixo visual + aninhamento
    //    na URL com a chave). Aplica enquanto o comentário foi gerado pelo
    //    backend antigo (chamados criados pré-fix de 28/05/2026 noite).
    if (fotoR2Key) {
      const escapedKey = fotoR2Key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Linha inteira que menciona "Foto anexada: <key>" / "Foto anexada (...)"
      cleaned = cleaned.replace(
        /^.*Foto anexada.*$/gm,
        '',
      );
      // Defesa: se sobrou o key em algum outro lugar, troca por placeholder
      cleaned = cleaned.replace(new RegExp(`\`${escapedKey}\``, 'g'), '');
      cleaned = cleaned.replace(new RegExp(escapedKey, 'g'), '');
    }

    // 3) Compacta múltiplas quebras vazias (3+ \n → 2)
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

    // 4) Renderiza markdown sanitizado (DOMPurify embutido)
    return renderMarkdown(cleaned);
  }, [corpo, fotoR2Key, users]);

  if (!html) return null;

  return (
    <div
      className="cb-comment-body text-sm text-ink-1 dark:text-ink-dark-1 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
